import { useAgendasList } from "@/hooks/useAgendasList";
import { useAttendancesList } from "@/hooks/useAttendancesList";
import { getAttendanceInitials } from "@/utils/getAttendanceInitials";
import { AttendeeListItem, AgendaItemDetails, AttendanceUser } from "@/models/prepareRecording";
import { useEffect, useMemo, useState } from "react";

export interface AttendancesListData {
  internalUsers: AttendanceUser[];
  externalUsers: AttendanceUser[];
}

const emptyAttendancesList: AttendancesListData = {
  internalUsers: [],
  externalUsers: [],
};

export interface RecordingSetupSelection {
  agendasList: AgendaItemDetails[];
  isLoadingAgendas: boolean;
  selectedAgendaId: string;
  setSelectedAgendaId: (agendaId: string) => void;
  selectedAgenda: AgendaItemDetails | null;
  attendeesList: AttendeeListItem[];
  isLoadingAttendees: boolean;
  selectedAttendees: AttendeeListItem[];
  setSelectedAttendees: React.Dispatch<React.SetStateAction<AttendeeListItem[]>>;
  toggleSelectedAttendeeId: (attendeeId: string) => void;
  selectedLanguage: string;
  setSelectedLanguage: React.Dispatch<React.SetStateAction<string>>;
}

// Shared by PrepareRecordingProvider and UploadRecordingProvider: agenda + attendee + language
// selection is identical setup data for both a live recording and an upload, only what happens
// after (mic recording vs. file upload) differs.
export function useRecordingSetupSelection(): RecordingSetupSelection {
  const { data: agendasList = [], isLoading: isLoadingAgendas } = useAgendasList();
  const [selectedAgendaId, setSelectedAgendaId] = useState("");
  const [selectedAttendees, setSelectedAttendees] = useState<AttendeeListItem[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("nl");

  const { data: attendancesData = emptyAttendancesList, isLoading: isLoadingAttendees } = useAttendancesList(selectedAgendaId, {
    enabled: !!selectedAgendaId,
  });

  useEffect(() => {
    setSelectedAttendees([]);
  }, [selectedAgendaId]);

  const selectedAgenda = useMemo(() => {
    return agendasList.find((agendaItem) => agendaItem.id === selectedAgendaId) ?? null;
  }, [agendasList, selectedAgendaId]);

  const attendeesList: AttendeeListItem[] = useMemo(() => {
    if (!attendancesData) return [];
    const allAttendees = [...attendancesData.internalUsers, ...attendancesData.externalUsers];

    return allAttendees.map((attendee) => ({
      ...attendee,
      initials: getAttendanceInitials(attendee.name) || "",
    }));
  }, [attendancesData]);

  const toggleSelectedAttendeeId = (attendeeId: string) => {
    setSelectedAttendees((currentSelectedAttendees) => {
      if (currentSelectedAttendees.some((attendee) => attendee.id === attendeeId)) {
        return currentSelectedAttendees.filter((attendee) => attendee.id !== attendeeId);
      }

      const newAttendee = attendeesList.find((attendee) => attendee.id === attendeeId);
      if (newAttendee) {
        return [...currentSelectedAttendees, newAttendee];
      }

      return currentSelectedAttendees;
    });
  };

  return {
    agendasList,
    isLoadingAgendas,
    selectedAgendaId,
    setSelectedAgendaId,
    selectedAgenda,
    attendeesList,
    isLoadingAttendees,
    selectedAttendees,
    setSelectedAttendees,
    toggleSelectedAttendeeId,
    selectedLanguage,
    setSelectedLanguage,
  };
}
